/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { Component, EventEmitter, Output } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FIPApiService } from '../../../../../shared/http/fip.api.service';
import { FipModel, IpGroupModel, NetworkInterfaceModel } from '../../../../../shared/models';
import { FloatingIpsService, LbErrorService, NetworkingService } from '../../../../../shared/services/components';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    UtilsService
} from '../../../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { ipValidator } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-fips',
    templateUrl: './add-edit-fip.component.html',
    styleUrls: ['../../../../../../../../assets/shared/scss/embedded.scss'],
})

export class AddEditFipComponent extends BaseComponent {
    /**
     * Selected IP ID.
     */
    @Output() public fipId: EventEmitter<string> = new EventEmitter<string>();

    /**
     * New IP FormGroup.
     */
    public ipForm: FormGroup;

    /**
     * Store for dropdown content.
     */
    public dropdownGroups: DropdownModel[] = [];

    /**
     * IP groups array.
     */
    public groups: IpGroupModel[] = [];

    /**
     * Floating IP objects.
     */
    public ips: FipModel[] = [];

    /**
     * Interfaces
     */
    public interfaces: NetworkInterfaceModel[] = [];

    /**
     * Store the id of fip being edited.
     */
    public editId: string = null;

    /**
     * Determines the way we style the component in and out of the wizard.
     */
    public componentEmbedded = false;

    /**
     * Group info message.
     */
    public groupMessage: any = null;

    /**
     * To store the callback for the wizard once the add is complete.
     * @private
     */
    private callBack: (...args: any[]) => void;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        public modalService: ModalService,
        public navigationService: NavigationService,
        private fipService: FIPApiService,
        private floatingIpsService: FloatingIpsService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private networkingService: NetworkingService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroups();
        this.setupFormSubscribers();
        this.setUpPageData();
    }

    /**
     * Link to groups page.
     */
    public linkToGroups(): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/network/ip-groups')).then().catch();
    }

    /**
     * Navigate back to the correct page.
     */
    public cancelButton() {
        if (this.componentEmbedded) {
            this.callBack();
        } else {
            this.navigateToURL('/network/floating/ips');
        }
    }

    /**
     * Ip check before submit.
     */
    public submitIpCheck(): void | Observable<any> {
        if (this.ipForm.get('assigned').value) {
            this.confirmIPChange();
        } else {
            return new Observable(observer => {
                this.submitIP()
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: () => observer.next(),
                        error: (error: ErrorResponseModel) => observer.error(error),
                        complete: () => observer.complete()
                    });
            });
        }
    }

    /**
     * Confirm ip change.
     * @private
     */
    private confirmIPChange(): void {
        const confirmationObject = {
            title: 'floating_ip_change',
            message: 'floating_ip_change_message',
            options: { translateText: true },
            callBack: this.submitIP.bind(this)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string) {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }

    /**
     * Add/Edit IPs.
     * @private
     */
    private submitIP(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const ipId = this.ipForm.get('id').value;
            const ip = this.ipForm.get('ip').value;
            const group = this.ipForm.get('group').value;
            const enabled = !!this.ipForm.get('enabled').value;

            const apiCall: Observable<any> = ipId
                ? this.fipService.editIP(ipId, { ip, groupId: group, enabled })
                : this.fipService.addIP({ ip, groupId: group, enabled });
            apiCall.pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (fip: FipModel): void => {
                        if (this.componentEmbedded) {
                            this.callBack(fip.id ? fip.id : ipId, enabled);
                            observer.next();
                            observer.complete();
                        } else {
                            this.navigateToURL('/network/floating/ips');
                            observer.next();
                            observer.complete();
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.ipForm, error.error.errors, error.uId);
                        const message: string = error && error.message ? error.message : 'Error submitting IP address';
                        this.logService.error('submitIP', message);

                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingButtonEmitter.next({ id: 'floating-ip-submit', state: false });
                    }
                });
        });
    }

    /**
     * Setup Floating IP form.
     * @private
     */
    private setupFormGroups(): void {
        this.ipForm = this.formBuilder.group({
            id: [null], // <-- used on edit
            ip: [null, [Validators.required, ipValidator]],
            group: [null, [Validators.required]],
            assigned: [null],
            enabled: [null]
        });
        this.initialFormData = this.ipForm.value;
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        const group: AbstractControl = this.ipForm.get('group');
        const formIp: AbstractControl = this.ipForm.get('ip');

        this.ipForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    if (this.editId) {
                        this.utilsService.debounce('form', () => {
                            if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                                this.ipForm.markAsPristine();
                            }
                        });
                    }
                }
            });

        group.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (groupId: string): void => {
                    this.utilsService.debounce('staticGroup', (): void => {
                        const selectedGroup: IpGroupModel = this.groups.find((item: IpGroupModel): boolean => item.id === groupId);
                        this.groupMessage = { ipRange: `${selectedGroup.ipRange.first} - ${selectedGroup.ipRange.last}` };
                        if (!this.ipForm.get('id').value) {
                            const ip: string = this.utilsService.getIpLead(selectedGroup);
                            formIp.setValue(ip);
                            formIp.markAsPristine();
                            formIp.markAsUntouched();
                        }
                    }, 500);
                }
            });

        formIp.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (ipAddress: any): void => {
                    this.utilsService.debounce('addFloatingIp', () => {
                        if (ipAddress !== this.initialFormData.ip) {
                            this.checkForDuplicateIpAddress(ipAddress, formIp);
                        }
                    }, 500);
                }
            });
    }

    /**
     * Set the error for a duplicate IP address.
     * @param ipAddress
     * @param formIp
     * @private
     */
    private checkForDuplicateIpAddress(ipAddress: string, formIp: AbstractControl): void {
        const match = this.ips.find(item => item.ip === ipAddress && item.id !== this.editId);
        if (match) {
            formIp.setErrors({ 'duplicateIP': `${this.languageService.translate('duplicate_group_ip')}` });
            formIp.markAsTouched();
        }
    }

    /**
     * Set the router data in the class variables.
     * @private
     */
    private setUpPageData(): void {
        this.floatingIpsService.getFloatingIpData()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: data => {
                    this.groups = data['groups'];
                    this.ips = data['ips'];
                    this.interfaces = data['interfaces'];

                    if (!this.groups.length) {
                        this.modalService.openModalInParent('noIPGroupsModal');
                    } else {
                        this.dropdownGroups = this.getDropDownGroups();
                        this.setUpPage();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditComponent', 'setUpPageData()', error.message);
                }
            });
    }

    /**
     * Get our structured object array for our dropdown.
     * @private
     */
    private getDropDownGroups(): DropdownModel[] {
        const groups: DropdownModel[] = [];
        for (const group of this.groups) {
            const assignedInterface = this.networkingService.getLocalAssignedInterface(group, this.interfaces);
            if (assignedInterface) {
                groups.push(<DropdownModel>{
                    key: group.id,
                    value: `${group.name} (${assignedInterface.settings.interfaceName})`
                });
            }
        }

        return groups;
    }

    /**
     * Set up the page based on id.
     * @private
     */
    private setUpPage(): void {
        this.editId = this.editId || this.route.snapshot.paramMap.get('id');
        if (this.editId) {
            const selectedIp = this.ips.find(item => item.id === this.editId);
            this.patchForm(selectedIp);
        }
    }

    /**
     * patch the selected data in the form.
     * @param selectedIp
     * @private
     */
    private patchForm(selectedIp: FipModel): void {
        const editFormData = {
            id: selectedIp.id,
            ip: selectedIp.ip,
            group: selectedIp.groupId,
            assigned: selectedIp?.usage.length > 0,
            enabled: selectedIp.enabled
        };

        this.ipForm.patchValue(editFormData, { emitEvent: false });
        this.initialFormData = this.ipForm.value;
    }
}
